import { useState } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useRouter } from 'src/routes/hooks';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetOrganizationContactsQuery } from 'src/services/contacts/contacts.service';
import {
  SigningMode,
  SignerInput,
  SigningOriginType,
} from 'src/services/signing/signing.types';
import {
  useAddSigningSignersMutation,
  useUploadSigningSourceMutation,
  useCreateSigningRequestMutation,
  useSetSigningSourceFromFileMutation,
} from 'src/services/signing/signing.service';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export interface SendForSigningDocument {
  file_id: string;
  name: string;
  mime?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  originType: SigningOriginType;
  originId: string;
  documents?: SendForSigningDocument[];
  documentsLoading?: boolean;
  defaultName?: string;
  defaultSigners?: SignerInput[];
}

const emptySigner = (): SignerInput => ({
  first_name: '',
  last_name: '',
  email: '',
  signing_order: 1,
  is_sender: false,
});

export default function SendForSigningModal({
  open,
  onClose,
  originType,
  originId,
  documents = [],
  documentsLoading,
  defaultName,
  defaultSigners,
}: Props) {
  const router = useRouter();
  const { user, organization } = useAuthContext();

  const [sourceTab, setSourceTab] = useState<'existing' | 'upload'>(documents.length ? 'existing' : 'upload');
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [signers, setSigners] = useState<SignerInput[]>(
    defaultSigners?.length ? defaultSigners : [emptySigner()]
  );
  const [mode, setMode] = useState<SigningMode>(SigningMode.PARALLEL);

  const [contactSearch, setContactSearch] = useState('');

  const [createRequest, { isLoading: creating }] = useCreateSigningRequestMutation();
  const [uploadSource, { isLoading: uploading }] = useUploadSigningSourceMutation();
  const [setSourceFromFile, { isLoading: settingSource }] = useSetSigningSourceFromFileMutation();
  const [addSigners, { isLoading: addingSigners }] = useAddSigningSignersMutation();

  const { data: contactsData } = useGetOrganizationContactsQuery(
    { org_id: organization.id, page: { page: 1, per_page: 20 }, search_query: contactSearch },
    { skip: contactSearch.length < 2 }
  );

  const pdfDocuments = documents.filter((d) => !d.mime || d.mime === 'application/pdf');

  const submitting = creating || uploading || settingSource || addingSigners;

  const updateSigner = (idx: number, patch: Partial<SignerInput>) => {
    setSigners((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeSigner = (idx: number) => {
    setSigners((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, signing_order: i + 1 })));
  };

  const moveSigner = (idx: number, dir: -1 | 1) => {
    setSigners((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((s, i) => ({ ...s, signing_order: i + 1 }));
    });
  };

  const addMe = () => {
    setSigners((prev) => [
      ...prev,
      {
        first_name: user?.first_name ?? '',
        last_name: user?.last_name ?? '',
        email: user?.email ?? '',
        is_sender: true,
        signing_order: prev.length + 1,
      },
    ]);
  };

  const addContact = (contact: { id: string; first_name: string; last_name: string; email: string }) => {
    setSigners((prev) => [
      ...prev,
      {
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        contact_id: contact.id,
        signing_order: prev.length + 1,
      },
    ]);
  };

  const canContinue =
    signers.length > 0 &&
    signers.every((s) => s.first_name && s.last_name && s.email) &&
    (sourceTab === 'existing' ? !!selectedFileId : !!uploadFile);

  const handleContinue = async () => {
    const name = defaultName || uploadFile?.name || pdfDocuments.find((d) => d.file_id === selectedFileId)?.name || 'Document';

    const request = await createRequest({
      name,
      origin_entity_type: originType,
      origin_entity_id: originId,
      signing_mode: mode,
    }).unwrap();

    if (sourceTab === 'upload' && uploadFile) {
      await uploadSource({ id: request.id, file: uploadFile }).unwrap();
    } else if (sourceTab === 'existing' && selectedFileId) {
      await setSourceFromFile({ id: request.id, file_id: selectedFileId }).unwrap();
    }

    await addSigners({
      id: request.id,
      signers: signers.map((s, i) => ({ ...s, signing_order: i + 1 })),
    }).unwrap();

    onClose();
    router.push(`/dashboard/signing/${request.id}/prepare`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Send for Signing</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Source */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Document
            </Typography>
            <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v)} sx={{ mb: 2 }}>
              <Tab value="existing" label="Select existing" />
              <Tab value="upload" label="Upload new" />
            </Tabs>

            {sourceTab === 'existing' ? (
              <TextField
                select
                fullWidth
                label="Existing PDF"
                value={selectedFileId}
                onChange={(e) => setSelectedFileId(e.target.value)}
                disabled={documentsLoading}
                helperText={!pdfDocuments.length ? 'No PDF documents found on this record' : ''}
              >
                {pdfDocuments.map((doc) => (
                  <MenuItem key={doc.file_id} value={doc.file_id}>
                    {doc.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Button variant="outlined" component="label" startIcon={<Iconify icon="eva:upload-fill" />}>
                {uploadFile ? uploadFile.name : 'Choose PDF'}
                <input
                  hidden
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            )}
          </Box>

          <Divider />

          {/* Signers */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Signers</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, v) => v && setMode(v)}
              >
                <ToggleButton value={SigningMode.PARALLEL}>Parallel</ToggleButton>
                <ToggleButton value={SigningMode.SEQUENTIAL}>Sequential</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack spacing={2}>
              {signers.map((signer, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  {mode === SigningMode.SEQUENTIAL && (
                    <Chip size="small" label={`#${idx + 1}`} />
                  )}
                  <TextField
                    label="First name"
                    size="small"
                    value={signer.first_name}
                    onChange={(e) => updateSigner(idx, { first_name: e.target.value })}
                  />
                  <TextField
                    label="Last name"
                    size="small"
                    value={signer.last_name}
                    onChange={(e) => updateSigner(idx, { last_name: e.target.value })}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    value={signer.email}
                    onChange={(e) => updateSigner(idx, { email: e.target.value })}
                  />
                  {signer.is_sender && <Chip size="small" color="info" label="You" />}
                  {mode === SigningMode.SEQUENTIAL && (
                    <>
                      <IconButton size="small" onClick={() => moveSigner(idx, -1)} disabled={idx === 0}>
                        <Iconify icon="eva:arrow-up-fill" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveSigner(idx, 1)}
                        disabled={idx === signers.length - 1}
                      >
                        <Iconify icon="eva:arrow-down-fill" />
                      </IconButton>
                    </>
                  )}
                  <IconButton size="small" color="error" onClick={() => removeSigner(idx)}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap">
              <Button size="small" startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => setSigners((p) => [...p, { ...emptySigner(), signing_order: p.length + 1 }])}>
                Add signer
              </Button>
              <Button size="small" startIcon={<Iconify icon="mdi:account-plus" />} onClick={addMe}>
                Add me
              </Button>
              <Autocomplete
                size="small"
                sx={{ minWidth: 240 }}
                options={(contactsData?.results ?? []) as any[]}
                getOptionLabel={(o) => `${o.first_name} ${o.last_name} (${o.email})`}
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => value && addContact(value)}
                renderInput={(params) => <TextField {...params} label="Add existing contact" />}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={submitting} disabled={!canContinue} onClick={handleContinue}>
          Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
